import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Pencil, Trash2, ToggleLeft, Tag, UserCog, ArrowUp, ArrowDown, ArrowUpDown, Clock, MoreHorizontal, CheckCircle2, XCircle, Bot } from 'lucide-react';

const BaseTable = ({
    data = [],
    columns = [],
    loading = false,
    onEdit,
    onDelete,
    onView,
    onApprove,
    onReject,
    onUpdateStatus,
    onUpdateCategory,
    onAssign,
    onExtension,
    onAiLearn,
    canDelete,
    canEdit,
    canView,
    canApprove,
    canReject,
    canUpdateStatus,
    canUpdateCategory,
    canAssign,
    canExtension,
    canAiLearn,
    viewIcon = null,
    pagination = null,
    onPageChange,
    showActions = true,
    actionColumnWidth = "80px",
    emptyMessage = "Không có dữ liệu",
    className = "",
    sortState = null,
    onSort
}) => {
    const scrollContainerRef = useRef(null);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);

    useEffect(() => {
        if (!activeMenu) return;

        const handleClose = () => setActiveMenu(null);
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setActiveMenu(null);
        };

        document.addEventListener('click', handleClose);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('scroll', handleClose, true);
        window.addEventListener('resize', handleClose);

        return () => {
            document.removeEventListener('click', handleClose);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('scroll', handleClose, true);
            window.removeEventListener('resize', handleClose);
        };
    }, [activeMenu]);

    const handleToggleMenu = (e, item, rowKey) => {
        e.stopPropagation();
        if (activeMenu?.rowKey === rowKey) {
            setActiveMenu(null);
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const menuHeight = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

        setActiveMenu({
            item,
            rowKey,
            style: {
                position: 'fixed',
                top: openUpward ? `${rect.top - 6}px` : `${rect.bottom + 6}px`,
                right: `${Math.max(12, window.innerWidth - rect.right)}px`,
                transform: openUpward ? 'translateY(-100%)' : 'none',
                zIndex: 99999,
            }
        });
    };

    const checkScrollEnd = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const atRightEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 3;
        const noScroll = el.scrollWidth <= el.clientWidth;
        setIsAtEnd(atRightEnd || noScroll);
    }, []);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const timer = setTimeout(checkScrollEnd, 50);

        el.addEventListener('scroll', checkScrollEnd, { passive: true });
        let resizeObserver;
        if (window.ResizeObserver) {
            resizeObserver = new ResizeObserver(checkScrollEnd);
            resizeObserver.observe(el);
        } else {
            window.addEventListener('resize', checkScrollEnd, { passive: true });
        }

        return () => {
            clearTimeout(timer);
            el.removeEventListener('scroll', checkScrollEnd);
            if (resizeObserver) resizeObserver.disconnect();
            else window.removeEventListener('resize', checkScrollEnd);
        };
    }, [checkScrollEnd, data, columns]);

    const stickyShadowClass = `before:absolute before:top-0 before:bottom-0 before:left-0 before:w-6 before:-translate-x-full before:bg-gradient-to-l before:from-black/15 before:to-transparent before:pointer-events-none before:transition-opacity before:duration-300 before:ease-in-out ${isAtEnd ? 'before:opacity-0 shadow-none' : 'before:opacity-100 shadow-[-6px_0_12px_-2px_rgba(0,0,0,0.12)]'}`;

    // Sort opt-in: chỉ kích hoạt cho cột có `sortable` + `sortKey` và khi có onSort.
    const getColumnOrder = (column) => {
        if (!sortState || sortState.key !== column.sortKey) return null;
        return sortState.order || null;
    };

    const handleHeaderSort = (column) => {
        if (!column.sortable || !column.sortKey || !onSort) return;
        const current = getColumnOrder(column);
        const next = current === 'asc' ? 'desc' : current === 'desc' ? null : 'asc';
        onSort(column.sortKey, next);
    };

    const renderSortIcon = (column) => {
        const order = getColumnOrder(column);
        if (order === 'asc') return <ArrowUp className="w-3.5 h-3.5 text-blue-600" />;
        if (order === 'desc') return <ArrowDown className="w-3.5 h-3.5 text-blue-600" />;
        return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />;
    };
    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-xs border border-gray-200">
                <div className="p-8 text-center">
                    <div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    <p className="mt-2 text-gray-500">Đang tải...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden ${className}`}>
            <div className="overflow-x-auto" ref={scrollContainerRef}>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((column, index) => {
                                const isStickyRight = column.sticky === 'right' || column.key === 'actions';
                                const thClassList = [
                                    'px-4',
                                    'py-3',
                                    'text-left',
                                    'text-xs',
                                    'font-medium',
                                    'text-gray-500',
                                    'uppercase',
                                    'tracking-wider',
                                    'whitespace-nowrap',
                                ];
                                if (column.width) thClassList.push(`w-${column.width}`);
                                if (isStickyRight) {
                                    thClassList.push(`sticky right-0 bg-gray-50 z-20 border-l border-gray-300 ${stickyShadowClass}`);
                                }

                                return (
                                    <th
                                        key={column.key || index}
                                        className={thClassList.join(' ')}
                                        style={column.width ? { width: column.width, maxWidth: column.width, minWidth: column.width } : {}}
                                    >
                                        {column.sortable && column.sortKey && onSort ? (
                                            <button
                                                type="button"
                                                onClick={() => handleHeaderSort(column)}
                                                className={`group inline-flex items-center gap-1 uppercase tracking-wider select-none hover:text-gray-700 ${getColumnOrder(column) ? 'text-blue-600' : ''}`}
                                                title="Sắp xếp"
                                            >
                                                <span>{column.title}</span>
                                                {renderSortIcon(column)}
                                            </button>
                                        ) : (
                                            column.title
                                        )}
                                    </th>
                                );
                            })}
                            {showActions && (
                                <th
                                    className={`sticky right-0 bg-gray-50 z-20 border-l border-gray-300 ${stickyShadowClass} px-3 text-center py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`}
                                    style={{ width: actionColumnWidth, maxWidth: actionColumnWidth, minWidth: actionColumnWidth }}
                                >
                                    Thao tác
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length + (showActions ? 1 : 0)}
                                    className="px-6 py-8 text-center text-gray-500"
                                >
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            data.map((item, rowIndex) => (
                                <tr key={item.id || rowIndex} className="group hover:bg-gray-50">
                                    {columns.map((column, colIndex) => {
                                        const isStickyRight = column.sticky === 'right' || column.key === 'actions';
                                        const customClassName = typeof column.className === 'function'
                                            ? column.className(item, rowIndex)
                                            : column.className;

                                        const tdClassList = [
                                            'px-4',
                                            'py-3.5',
                                            'whitespace-nowrap',
                                            'text-sm',
                                            'text-gray-900'
                                        ];
                                        if (customClassName) tdClassList.push(customClassName);
                                        if (isStickyRight) {
                                            tdClassList.push(`sticky right-0 bg-white group-hover:bg-gray-50 border-l border-gray-300 ${stickyShadowClass}`);
                                            if (!customClassName || !customClassName.includes('z-')) {
                                                tdClassList.push('z-10');
                                            }
                                        }

                                        return (
                                            <td
                                                key={column.key || colIndex}
                                                className={tdClassList.join(' ')}
                                                style={column.width ? { width: column.width, maxWidth: column.width, minWidth: column.width } : {}}
                                            >
                                                {column.render ? column.render(item[column.dataIndex], item, rowIndex) : item[column.dataIndex]}
                                            </td>
                                        );
                                    })}
                                    {showActions && (
                                        <td
                                            className={`sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-300 ${stickyShadowClass} px-3 py-3 whitespace-nowrap text-center text-sm font-medium`}
                                            style={{ width: actionColumnWidth, maxWidth: actionColumnWidth, minWidth: actionColumnWidth }}
                                        >
                                            {(() => {
                                                const hasView = Boolean(onView && (!canView || canView(item)));
                                                const hasApprove = Boolean(onApprove && (!canApprove || canApprove(item)));
                                                const hasReject = Boolean(onReject && (!canReject || canReject(item)));
                                                const hasEdit = Boolean(onEdit && (!canEdit || canEdit(item)));
                                                const hasUpdateStatus = Boolean(onUpdateStatus && (!canUpdateStatus || canUpdateStatus(item)));
                                                const hasUpdateCategory = Boolean(onUpdateCategory && (!canUpdateCategory || canUpdateCategory(item)));
                                                const hasAssign = Boolean(onAssign && (!canAssign || canAssign(item)));
                                                const hasExtension = Boolean(onExtension && (!canExtension || canExtension(item)));
                                                const hasDelete = Boolean(onDelete && (!canDelete || canDelete(item)));

                                                const hasAnyAction = hasView || hasApprove || hasReject || hasEdit || hasUpdateStatus || hasUpdateCategory || hasAssign || hasExtension || hasDelete;
                                                const rowKey = item.id ?? rowIndex;
                                                const isOpen = activeMenu?.rowKey === rowKey;

                                                if (!hasAnyAction) {
                                                    return <span className="text-gray-300 text-xs">-</span>;
                                                }

                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleToggleMenu(e, item, rowKey)}
                                                        className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                                                            isOpen
                                                                ? 'bg-blue-100 text-blue-700'
                                                                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                                                        }`}
                                                        title="Thao tác"
                                                    >
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* FLOATING ACTION MENU PORTAL */}
            {activeMenu && typeof document !== 'undefined' && createPortal(
                <div
                    style={activeMenu.style}
                    onClick={(e) => e.stopPropagation()}
                    className="w-52 bg-white rounded-lg shadow-2xl border border-gray-200 py-1.5 animate-in fade-in zoom-in-95 duration-100 select-none"
                >
                    {(() => {
                        const item = activeMenu.item;
                        const hasView = Boolean(onView && (!canView || canView(item)));
                        const hasApprove = Boolean(onApprove && (!canApprove || canApprove(item)));
                        const hasReject = Boolean(onReject && (!canReject || canReject(item)));
                        const hasEdit = Boolean(onEdit && (!canEdit || canEdit(item)));
                        const hasUpdateStatus = Boolean(onUpdateStatus && (!canUpdateStatus || canUpdateStatus(item)));
                        const hasUpdateCategory = Boolean(onUpdateCategory && (!canUpdateCategory || canUpdateCategory(item)));
                        const hasAssign = Boolean(onAssign && (!canAssign || canAssign(item)));
                        const hasExtension = Boolean(onExtension && (!canExtension || canExtension(item)));
                        const hasAiLearn = Boolean(onAiLearn && (!canAiLearn || canAiLearn(item)));
                        const hasDelete = Boolean(onDelete && (!canDelete || canDelete(item)));

                        return (
                            <>
                                {hasView && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onView(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors cursor-pointer text-left"
                                    >
                                        {viewIcon ? viewIcon : <Eye className="w-4 h-4 text-blue-600 shrink-0" />}
                                        <span>Xem chi tiết</span>
                                    </button>
                                )}
                                {hasApprove && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onApprove(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 transition-colors cursor-pointer text-left font-medium"
                                    >
                                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                        <span>Phê duyệt đơn</span>
                                    </button>
                                )}
                                {hasReject && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onReject(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer text-left font-medium"
                                    >
                                        <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                                        <span>Từ chối đơn</span>
                                    </button>
                                )}
                                {hasEdit && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onEdit(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-colors cursor-pointer text-left"
                                    >
                                        <Pencil className="w-4 h-4 text-indigo-600 shrink-0" />
                                        <span>Chỉnh sửa</span>
                                    </button>
                                )}
                                {hasUpdateStatus && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onUpdateStatus(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-amber-600 transition-colors cursor-pointer text-left"
                                    >
                                        <ToggleLeft className="w-4 h-4 text-amber-600 shrink-0" />
                                        <span>Cập nhật trạng thái</span>
                                    </button>
                                )}
                                {hasAiLearn && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onAiLearn(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors cursor-pointer text-left"
                                    >
                                        <Bot className="w-4 h-4 text-purple-600 shrink-0" />
                                        <span>{item.ai_da_hoc ? "Thu hồi khỏi AI" : "Cho AI học"}</span>
                                    </button>
                                )}
                                {hasUpdateCategory && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onUpdateCategory(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-emerald-600 transition-colors cursor-pointer text-left"
                                    >
                                        <Tag className="w-4 h-4 text-emerald-600 shrink-0" />
                                        <span>Cập nhật lĩnh vực</span>
                                    </button>
                                )}
                                {hasAssign && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onAssign(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 transition-colors cursor-pointer text-left"
                                    >
                                        <UserCog className="w-4 h-4 text-purple-600 shrink-0" />
                                        <span>Chuyển chuyên viên</span>
                                    </button>
                                )}
                                {hasExtension && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveMenu(null);
                                            onExtension(item);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-amber-600 transition-colors cursor-pointer text-left"
                                    >
                                        <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                                        <span>Xin gia hạn thời gian</span>
                                    </button>
                                )}
                                {hasDelete && (
                                    <>
                                        <div className="my-1 border-t border-gray-100" />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActiveMenu(null);
                                                onDelete(item);
                                            }}
                                            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer text-left"
                                        >
                                            <Trash2 className="w-4 h-4 text-red-600 shrink-0" />
                                            <span>Xóa</span>
                                        </button>
                                    </>
                                )}
                            </>
                        );
                    })()}
                </div>,
                document.body
            )}

            {Boolean(
                pagination && (
                    (pagination.totalPages && pagination.totalPages > 1) ||
                    ((pagination.total || pagination.totalItems || 0) > (pagination.pageSize || 10))
                )
            ) && (
                <div className="bg-white px-3 py-2.5 sm:px-6 sm:py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex items-center justify-between sm:hidden">
                        <button
                            onClick={() => {
                                const handleChange = pagination.onChange || onPageChange;
                                handleChange && handleChange((pagination.current || pagination.currentPage) - 1);
                            }}
                            disabled={(pagination.current || pagination.currentPage) <= 1}
                            className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            Trước
                        </button>
                        <span className="text-xs font-medium text-gray-600 px-2">
                            Trang {pagination.current || pagination.currentPage || 1} / {pagination.totalPages || 1}
                        </span>
                        <button
                            onClick={() => {
                                const handleChange = pagination.onChange || onPageChange;
                                handleChange && handleChange((pagination.current || pagination.currentPage) + 1);
                            }}
                            disabled={(pagination.current || pagination.currentPage) >= pagination.totalPages}
                            className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            Sau
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                {(() => {
                                    const currentPage = pagination.current || pagination.currentPage || 1;
                                    const pageSize = pagination.pageSize || 10;
                                    const totalItems = pagination.total || pagination.totalItems || 0;

                                    if (totalItems === 0) {
                                        return <>Không có kết quả nào</>;
                                    }

                                    const startItem = ((currentPage - 1) * pageSize) + 1;
                                    const endItem = Math.min(currentPage * pageSize, totalItems);

                                    return (
                                        <>
                                            Hiển thị{' '}
                                            <span className="font-medium">{startItem}</span>
                                            {' '}đến{' '}
                                            <span className="font-medium">{endItem}</span>
                                            {' '}trong{' '}
                                            <span className="font-medium">{totalItems}</span>
                                            {' '}kết quả
                                        </>
                                    );
                                })()}
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => {
                                        const handleChange = pagination.onChange || onPageChange;
                                        handleChange && handleChange((pagination.current || pagination.currentPage) - 1);
                                    }}
                                    disabled={(pagination.current || pagination.currentPage) <= 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </button>

                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                    const currentPage = pagination.current || pagination.currentPage;
                                    let pageNum;
                                    if (pagination.totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= pagination.totalPages - 2) {
                                        pageNum = pagination.totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    const handleChange = pagination.onChange || onPageChange;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handleChange && handleChange(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === currentPage
                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => {
                                        const handleChange = pagination.onChange || onPageChange;
                                        handleChange && handleChange((pagination.current || pagination.currentPage) + 1);
                                    }}
                                    disabled={(pagination.current || pagination.currentPage) >= pagination.totalPages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BaseTable;